/* eslint-disable react-refresh/only-export-components */
import { cva, type VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes } from 'react';

import { cn, IconName, noddiColors, NoddiIcon } from 'noddi-ui-common';
import { NoddiSpinner } from '../atoms';
import { themeColors } from '../style';
import { webButtonStyling } from '../theme';

// Don't add hover as effect persists on mobile after pressed, which makes the button look disabled. ( Can do for over md > )
export const buttonVariants = cva(
  cn('flex size-fit items-center justify-center text-nowrap rounded-lg disabled:opacity-50', webButtonStyling),
  {
    variants: {
      variant: {
        primary: cn('border border-button-primary bg-button-primary text-primary-foreground'),
        secondary: cn('border border-primary bg-white text-primary'),
        ghost: cn(''),
        success: cn('border border-success bg-success text-white'),
        destructive: cn('border border-danger bg-danger text-white'),
        link: cn('text-primary'),
        lightLink: cn('text-primary-foreground')
      },
      size: {
        default: 'px-4 py-2',
        small: 'px-3 py-1'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default'
    }
  }
);

export const iconColorVariantsInNoddiButton = cva('', {
  variants: {
    variant: {
      primary: themeColors['primary-foreground'],
      secondary: themeColors.primary,
      ghost: noddiColors.black,
      success: noddiColors.white,
      destructive: noddiColors.white,
      link: themeColors.primary,
      lightLink: themeColors['primary-foreground']
    }
  },
  defaultVariants: {
    variant: 'primary'
  }
});

export const spinnerTextColorVariants = cva('', {
  variants: {
    variant: {
      primary: 'text-primary-foreground',
      secondary: 'text-primary',
      ghost: 'text-black',
      success: 'text-white',
      destructive: 'text-white',
      link: 'text-primary',
      lightLink: 'text-primary-foreground'
    }
  },
  defaultVariants: {
    variant: 'primary'
  }
});

export type NoddiButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    loading?: boolean;
  } & ({ startIcon?: IconName; endIcon?: never } | { endIcon?: IconName; startIcon?: never });

export const NoddiButton = ({
  className,
  variant,
  size,
  endIcon,
  startIcon,
  loading,
  children,
  ...props
}: NoddiButtonProps) => {
  const isSmall = size === 'small';
  const iconSize = isSmall ? 'small' : 'medium';
  const buttonClassName = cn(buttonVariants({ variant, size }), className);
  const iconColor = iconColorVariantsInNoddiButton({ variant });
  const spinnerTextColor = spinnerTextColorVariants({ variant });

  return (
    <button className={buttonClassName} type='button' {...props}>
      {loading ? (
        <NoddiSpinner className={spinnerTextColor} size='small' />
      ) : (
        <div className='flex flex-row items-center justify-center gap-2'>
          {startIcon && (
            <div>
              <NoddiIcon color={iconColor} name={startIcon} size={iconSize} />
            </div>
          )}
          <span className='text-left'>{children}</span>
          {endIcon && (
            <div>
              <NoddiIcon color={iconColor} name={endIcon} size={iconSize} />
            </div>
          )}
        </div>
      )}
    </button>
  );
};
